import { useEffect, useState, useCallback } from "react";
import { Shield, Loader2, ChevronDown, ChevronUp, RefreshCcw } from "lucide-react";
import api, { formatPrice } from "../lib/api";

/**
 * Phase 4.21 — Versicherungs-Schnellberechnung-Widget für ListingDetailPage.
 *
 * Berechnet aus listing.power_kw, listing.first_registration_date,
 * listing.condition und Default-SF1/2 die jährliche Versicherungs-
 * Annäherung. Schadenklasse ist via Dropdown anpassbar.
 */
const SF_OPTIONS = [
    "SF0", "SF1/2", "SF1", "SF2", "SF3", "SF4", "SF5", "SF6", "SF7", "SF8",
    "SF9", "SF10", "SF15", "SF20", "SF25", "SF30", "SF35",
];

export default function InsuranceQuickEstimate({ listing }) {
    const [open, setOpen] = useState(false);
    const [sf, setSf] = useState("SF1/2");
    const [region, setRegion] = useState((listing?.postal_code || "").slice(0, 5));
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const calculate = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.post("/insurance/estimate", {
                category: listing?.category || "motorcycle",
                power_kw: listing?.power_kw,
                first_registration_date: listing?.first_registration_date,
                condition: listing?.condition,
                schadenklasse: sf,
                region_plz: region,
            });
            setResult(r.data);
        } catch {
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [listing, sf, region]);

    useEffect(() => {
        if (open && !result) calculate();
    }, [open, calculate, result]);

    // Re-calc on SF / region change
    useEffect(() => {
        if (open) calculate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sf, region]);

    if (!listing || !listing.power_kw) return null;

    return (
        <div className="border border-gray-200 rounded-md bg-white" data-testid="insurance-estimate-widget">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 p-4 hover:bg-gray-50 transition-colors text-left"
                data-testid="insurance-estimate-toggle"
            >
                <Shield className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-base font-display font-bold">Versicherungs-Schnellberechnung</div>
                    <div className="text-xs text-gray-500">Orientierung für Haftpflicht · Teilkasko · Vollkasko (jährlich)</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm">MOCKED</span>
                {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {open && (
                <div className="px-5 pb-5 pt-1 border-t border-gray-100" data-testid="insurance-estimate-body">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 block mb-1">Schadenklasse</label>
                            <select
                                value={sf}
                                onChange={(e) => setSf(e.target.value)}
                                className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm font-mono-num"
                                data-testid="insurance-sf-select"
                            >
                                {SF_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 block mb-1">PLZ (Region)</label>
                            <input
                                value={region}
                                onChange={(e) => setRegion(e.target.value.replace(/\D/g, "").slice(0, 5))}
                                placeholder="z. B. 80331"
                                maxLength={5}
                                className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm font-mono-num"
                                data-testid="insurance-plz-input"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center gap-2 justify-center py-6 text-sm text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Berechne…
                        </div>
                    ) : result ? (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="insurance-result">
                                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-center">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Haftpflicht</div>
                                    <div className="font-display font-black text-xl text-[#111827] font-mono-num mt-1">{formatPrice(result.liability_eur_yr)} / J.</div>
                                </div>
                                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-center">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Teilkasko</div>
                                    <div className="font-display font-black text-xl text-[#111827] font-mono-num mt-1">{formatPrice(result.partial_kasko_eur_yr)} / J.</div>
                                </div>
                                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-center">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Vollkasko</div>
                                    {result.full_kasko_eur_yr ? (
                                        <div className="font-display font-black text-xl text-[#111827] font-mono-num mt-1">{formatPrice(result.full_kasko_eur_yr)} / J.</div>
                                    ) : (
                                        <div className="text-xs text-gray-400 mt-2">nicht sinnvoll (Alter &gt; 12 J.)</div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-3 flex items-start gap-1.5 text-[11px] text-gray-500">
                                <RefreshCcw className="w-3 h-3 mt-0.5 shrink-0" />
                                <span>Faktoren: Alter {result.factors.years_old} J. · {result.factors.condition_factor.toFixed(2)} (Zustand) · {result.factors.sf_factor.toFixed(2)} (SF) · {result.factors.region_factor.toFixed(2)} (Region). {result.disclaimer}</span>
                            </div>
                        </>
                    ) : (
                        <div className="text-xs text-gray-500 py-4 text-center">Keine Berechnung verfügbar — Leistungsdaten fehlen am Inserat.</div>
                    )}
                </div>
            )}
        </div>
    );
}
